<script setup lang="ts">
import BaseButton from '@/components/BaseButton.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import router from '@/router'
import { useDatabaseStore } from '@/stores/databaseStore'
import type { Tables } from '@/types/database.types'
import { ref } from 'vue'

const props = defineProps<{ divisionId: string }>()

const databaseStore = useDatabaseStore()
const assemblyData = ref<Omit<Tables<'assembly'>, 'tenant_id'>>({
  id: '',
  name: '',
  assembly_division_id: props.divisionId,
  code: '',
  unit_of_measure_id: '',
  variables: {},
})
const division = databaseStore.assembyDivisions?.find((d) => d.id === props.divisionId)

async function addAssembly() {
  // TODO - Ensure inputs are valid before saving
  const newDBAssembly = await supabase
    .from('assembly')
    .insert({
      id: uuidv7(),
      name: assemblyData.value.name,
      assembly_division_id: props.divisionId,
      code: assemblyData.value.code,
      unit_of_measure_id: assemblyData.value.unit_of_measure_id,
    })
    .select()
    .single()
  if (newDBAssembly.error) throw toastThrowError('Unable to add assembly', newDBAssembly.error)
  databaseStore.assemblies?.push(newDBAssembly.data)
  await router.replace({
    name: 'DatabaseAssemblyEdit',
    params: { assemblyId: newDBAssembly.data.id },
  })
}
</script>

<template>
  <div class="flex h-full w-full flex-col gap-3 p-3">
    <div class="flex w-full items-center justify-between bg-gray-100 p-3 text-center">
      <h1 class="w-full">New Assembly</h1>
      <button class="cursor-pointer rounded-md p-2 hover:bg-gray-200" @click="router.back()">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="size-6"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
    <BaseInput
      name="Code"
      v-model="assemblyData.code"
      :formatter="(v) => `${division?.code}.${v}`"
    ></BaseInput>
    <BaseInput name="Name" v-model="assemblyData.name"></BaseInput>
    <BaseSelect
      :select-options="databaseStore.unitsOfMeasure"
      option-key="id"
      option-name="code"
      option-value="id"
      v-model="assemblyData.unit_of_measure_id"
      name="Unit"
    ></BaseSelect>
    <BaseButton @click="addAssembly()"> Add Item </BaseButton>
  </div>
</template>
